import { useEffect, useRef, useState, useCallback } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { apiCall } from '../services/api';

export const useVoiceClient = (userIdentity = 'dashboard_user') => {
  const deviceRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);

  // Fetch access token from backend. Tolerant of the Twilio voice endpoint
  // being absent or unconfigured (the most common case in dev / on tenants
  // that don't use voice). We return null so initializeDevice can no-op
  // cleanly without surfacing a runtime-error overlay.
  const fetchAccessToken = useCallback(async () => {
    try {
      const response = await apiCall('/voice/token', {
        method: 'POST',
        body: JSON.stringify({
          identity: userIdentity,
          ttl: 3600,
        }),
      });
      if (!response || !response.token) {
        return null;
      }
      return response.token;
    } catch (error) {
      // 404 / 501 / "Voice not configured" — silently skip device init.
      return null;
    }
  }, [userIdentity]);

  // Initialize the Twilio Device
  useEffect(() => {
    let isMounted = true;
    
    const initializeDevice = async () => {
      try {
        const token = await fetchAccessToken();

        if (!isMounted) return;
        // No token (Twilio not configured or endpoint missing) — silently
        // skip device setup. Voice features will just remain inactive.
        if (!token) {
          setIsReady(false);
          return;
        }

        // Create new Device instance
        const device = new Device(token, {
          logLevel: 1,
          codecPreferences: ['opus', 'pcmu'],
        });

        // Register device event handlers
        device.on('registered', () => {
          console.log('📞 Twilio Voice Device registered and ready');
          if (isMounted) {
            setIsReady(true);
            setError(null);
          }
        });

        device.on('error', (error) => {
          console.error('📞 Voice Device error:', error);
          if (isMounted) {
            setError(`Device error: ${error.message}`);
          }
        });

        device.on('incoming', (call) => {
          console.log('📞 Incoming call from:', call.parameters.From);
          
          if (isMounted) {
            setIncomingCall(call);
          }

          // Handle call events
          call.on('disconnect', () => {
            console.log('📞 Incoming call disconnected');
            if (isMounted) {
              setIncomingCall(null);
              setActiveCall(null);
              setIsConnected(false);
            }
          });

          call.on('cancel', () => {
            console.log('📞 Incoming call cancelled');
            if (isMounted) {
              setIncomingCall(null);
            }
          });
        });

        // Register to receive incoming calls
        await device.register();
        
        deviceRef.current = device;
        
      } catch (error) {
        console.error('Failed to initialize voice device:', error);
        if (isMounted) {
          // Don't show error if it's just not configured / no token / network 4xx
          const msg = (error && error.message) || '';
          if (!error || msg.includes('not configured') || msg.includes('404') || msg.includes('501')) {
            setError(null);
          } else {
            setError(msg || 'Failed to initialize voice client');
          }
        }
      }
    };

    initializeDevice();

    return () => {
      isMounted = false;
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }
    };
  }, [fetchAccessToken]);

  // Accept incoming call
  const acceptCall = useCallback(async () => {
    if (incomingCall) {
      try {
        await incomingCall.accept();
        
        setActiveCall(incomingCall);
        setIncomingCall(null);
        setIsConnected(true);

        // Handle call disconnect
        incomingCall.on('disconnect', () => {
          setActiveCall(null);
          setIsConnected(false);
        });
        
      } catch (error) {
        console.error('Failed to accept call:', error);
        setError(`Failed to accept call: ${error.message}`);
      }
    }
  }, [incomingCall]);

  // Reject incoming call
  const rejectCall = useCallback(() => {
    if (incomingCall) {
      incomingCall.reject();
      setIncomingCall(null);
    }
  }, [incomingCall]);

  // Ignore call - sends to voicemail/VAPI by not answering
  // This rejects the call on the browser side, but Twilio will continue 
  // ringing and eventually forward to VAPI after the timeout
  const ignoreCall = useCallback(() => {
    if (incomingCall) {
      // Ignore the call (don't answer) - this lets Twilio's timeout kick in
      // and forward to VAPI voicemail
      console.log('📞 Ignoring call - will be forwarded to AI voicemail');
      incomingCall.ignore();  // Twilio SDK method to ignore without rejecting
      setIncomingCall(null);
    }
  }, [incomingCall]);

  // Make outgoing call
  const makeCall = useCallback(async (phoneNumber) => {
    if (!deviceRef.current) {
      setError('Voice device not ready');
      return;
    }

    try {
      console.log('📞 Making call to:', phoneNumber);
      
      // Request microphone permission first
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('📞 Microphone permission granted');
      } catch (micError) {
        console.error('📞 Microphone permission denied:', micError);
        setError('Microphone permission required for calls. Please allow microphone access.');
        return;
      }
      
      const call = await deviceRef.current.connect({
        params: {
          To: phoneNumber,
        },
      });

      console.log('📞 Call object created:', call);
      console.log('📞 Call status:', call.status());
      
      setActiveCall(call);
      setError(null);
      
      // Listen for when call is accepted (connected)
      call.on('accept', () => {
        console.log('📞 Call accepted/connected - audio should be flowing');
        setIsConnected(true);
      });

      call.on('disconnect', () => {
        console.log('📞 Call disconnected');
        setActiveCall(null);
        setIsConnected(false);
      });
      
      call.on('cancel', () => {
        console.log('📞 Call cancelled');
        setActiveCall(null);
        setIsConnected(false);
      });

      call.on('error', (error) => {
        console.error('📞 Call error:', error);
        setError(`Call error: ${error.message}`);
        setActiveCall(null);
        setIsConnected(false);
      });
      
      call.on('ringing', (hasEarlyMedia) => {
        console.log('📞 Call is ringing... hasEarlyMedia:', hasEarlyMedia);
      });
      
      // Listen for when audio is connected
      call.on('sample', (sample) => {
        // This fires when audio samples are being processed
        // Useful for debugging audio issues
      });
      
      call.on('volume', (inputVolume, outputVolume) => {
        // Audio levels - useful for debugging
        if (inputVolume > 0 || outputVolume > 0) {
          console.log('📞 Audio levels - Input:', inputVolume, 'Output:', outputVolume);
        }
      });
      
    } catch (error) {
      console.error('Failed to make call:', error);
      setError(`Failed to make call: ${error.message}`);
    }
  }, []);

  // End active call
  const endCall = useCallback(() => {
    if (activeCall) {
      activeCall.disconnect();
      setActiveCall(null);
      setIsConnected(false);
    }
  }, [activeCall]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (activeCall) {
      const newMuteState = !isMuted;
      activeCall.mute(newMuteState);
      setIsMuted(newMuteState);
    }
  }, [activeCall, isMuted]);

  return {
    isReady,
    isConnected,
    activeCall,
    incomingCall,
    error,
    isMuted,
    acceptCall,
    rejectCall,
    ignoreCall,
    makeCall,
    endCall,
    toggleMute,
    device: deviceRef.current,
  };
};

export default useVoiceClient;
